/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { showDebug } from '../../helpers/debug/debug';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { ParentalControlLockState } from '../../helpers/parentalControl/constsAndTypes';
import './ImageCarousel.css';

const ImageCarousel = (): React.Node => {
  const gridSectionId = useSelector<string | null>((state) => state.ui.gridSectionId);
  const isDebugModeEnabled = useSelector<boolean>((state) => state.appConfiguration.isDebugModeEnabled);

  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [imageUrlList, setImageUrlList] = useState<Array<string> | null>(null);
  const [isHidden, setIsHidden] = useState<boolean>(gridSectionId !== null);
  const [lockedIndices, setLockedIndices] = useState<Set<number>>(new Set<number>());

  const localShowDebug = useCallback(() => {
    if (!isDebugModeEnabled) {
      return;
    }

    showDebug('Background Images', {
      misc: { currentIndex, imageUrlList, isHidden, lockedIndices },
    });
  }, [currentIndex, imageUrlList, isHidden, isDebugModeEnabled, lockedIndices]);

  const setImages = useCallback((imgUrlList: Array<string> | null) => {
    if (imgUrlList === null) {
      setLockedIndices(new Set<number>());
    }

    setCurrentIndex(0);
    setImageUrlList(imgUrlList);
  }, []);

  const show = useCallback(() => {
    setIsHidden(false);
  }, []);

  const hide = useCallback(() => {
    setIsHidden(true);
  }, []);

  const updateLockIndexState = useCallback((index: number, lockState: ParentalControlLockState) => {
    setLockedIndices((prevLockedIndices) => {
      const newLockedIndices = new Set(prevLockedIndices);

      if (lockState === ParentalControlLockState.Locked) {
        newLockedIndices.add(index);
      } else if (lockState === ParentalControlLockState.Unlocked) {
        newLockedIndices.delete(index);
      }

      return newLockedIndices;
    });
  }, []);

  useEffect(() => {
    Messenger.on(MessengerEvents.CAROUSEL_SHOW_DEBUG, localShowDebug);
    Messenger.on(MessengerEvents.IMAGE_CAROUSEL_SET, setImages);
    Messenger.on(MessengerEvents.CAROUSEL_SET_CURRENT_INDEX, setCurrentIndex);
    Messenger.on(MessengerEvents.CAROUSEL_ITEM_LOCK_STATE_UPDATE, updateLockIndexState);
    Messenger.on(MessengerEvents.CAROUSEL_SHOW, show);
    Messenger.on(MessengerEvents.CAROUSEL_HIDE, hide);

    return () => {
      Messenger.off(MessengerEvents.CAROUSEL_SHOW_DEBUG, localShowDebug);
      Messenger.off(MessengerEvents.IMAGE_CAROUSEL_SET, setImages);
      Messenger.off(MessengerEvents.CAROUSEL_SET_CURRENT_INDEX, setCurrentIndex);
      Messenger.off(MessengerEvents.CAROUSEL_ITEM_LOCK_STATE_UPDATE, updateLockIndexState);
      Messenger.off(MessengerEvents.CAROUSEL_SHOW, show);
      Messenger.off(MessengerEvents.CAROUSEL_HIDE, hide);
    };
  }, [localShowDebug, setImages, show, hide, updateLockIndexState]);

  if (isHidden || !imageUrlList || currentIndex < 0 || currentIndex >= imageUrlList.length) {
    return null;
  }

  return (
    <div className='imageCarousel'>
      {imageUrlList.map((url, i) => (
        <img alt='' className={clsx('imageContainer', i === currentIndex && 'selected', lockedIndices.has(i) && 'locked')} data-index={i} key={url} src={url || null} />
      ))}
      <div className='mask' />
    </div>
  );
};

export default ImageCarousel;
